import { type CaptureFileJSONObject, IkeToken } from '@ikegps/core-api';
import type { DistanceBearingPitch, LocationData, Point } from '@ikegps/core-api';
import { IkeUnit } from '@ikegps/core-shared';
import {
	CollectionModel,
	CollectionModelFieldAnchor,
	CollectionModelFieldVector,
	IkeGeodesic,
} from '@ikegps/core-shared';
import { noop, pick } from 'lodash';
import { get } from 'lodash';
import { action, observable, makeObservable } from 'mobx';
import { IMithrilComponent } from '../../components/mithril.component';
import { T, Translate } from '../../i18n';
import { addStyles, assetPath, DefaultTheme } from '../../components/theme';
import { m, type MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { classNames } from '../../util/classnames';
import { PointCache } from '../google/projection.cache';
import { MeasurementState, type MeasurementPoint } from '../../state/measurement.state';
import { DistanceMeasureState } from '../../state/distance.measure.state';
import { GenericMapPoint } from './map.point.generic';
import { GenericMapLine } from './map.line.generic';
import { MapLine } from './map.line/map.line';
import { MapPoint } from './map.point/map.point';
import { TopologicalPointCollectionCache } from './point.collection.cache';
import { AppStore } from '../../store';

const CSS = {
	container: 'c-PhotomeasureOverlay',
	orca: 'c-PhotomeasureOverlay__device',
	orcaIcon: 'c-PhotomeasureOverlay__orcaIcon',
	fov: 'c-PhotomeasureOverlay__fov',
	fovSprite: 'c-PhotomeasureOverlay__fovSprite',
	label: 'c-PhotomeasureOverlay__label',
	line: 'c-MapOverlayLayer__line',
	point: 'c-MapOverlayLayer__point',
	hidden: 'c-MapOverlayLayer--hidden',
};

const Style = {
	[CSS.container]: {
		position: 'absolute',
	},
	[CSS.orca]: {
		position: 'absolute',
		transform: 'translate3d(-16px, -16px, -1px)',
		width: '32px',
		height: '32px',
		borderRadius: '16px',
		boxShadow: DefaultTheme.shadow.box.dp4,
	},
	[CSS.orcaIcon]: {
		width: '32px',
		height: '32px',
		transformOrigin: '50% 50%',
	},
	[CSS.fov]: {
		position: 'absolute',
		left: 0,
		top: 0,
		transform: 'translate3d(-75px, -129px, -1px)',
	},
	[CSS.fovSprite]: {
		width: '150px',
		height: '129px',
		transformOrigin: '50% 100%',
	},
	[CSS.label]: {
		paddingTop: '20px',
	},
	[CSS.hidden]: {
		display: 'none',
	},
};

addStyles(Style);

interface ITopologicalEditorMapOverlayOptions {
	map: google.maps.Map;
	onCollectionClick: (id: string) => void;
	onVectorClick: (collectionId: string, vectorId: string) => void;
	selectedCollectionRevision?: string | null;
	collections?: CollectionModel[];
	onVectorDragStart?: () => void;
	onVectorDragEnd?: (
		field: CollectionModelFieldVector | CollectionModelFieldAnchor,
		nextVector: DistanceBearingPitch
	) => void;
	measurementState?: MeasurementState;
	distanceMeasureState?: DistanceMeasureState;
}

export class TopologicalEditorMapOverlay extends IMithrilComponent {
	private overlay: google.maps.OverlayView;
	private origin = new google.maps.LatLng(0, 0);
	private el: Element | null = null;

	@observable bounds: google.maps.LatLngBounds | undefined = undefined;
	boundString = observable.box('', { name: 'BoundsString' });
	@observable zoom: number | undefined = undefined;

	@observable private collections: CollectionModel[] = [];
	@observable private selectedCollectionRevision: string | null = null;
	@observable private selectedVectorId: string | null = null;
	@observable public visible = true;
	@observable public collectionHoverRevision: string | null = null;
	@observable public collectionAndVectorHoverId: string | null = null;
	@observable public viewCollectionLabels = false;
	@observable public viewVectorLabels = false;
	@observable public hideTitles = false;

	private measurementState?: MeasurementState;
	private distanceMeasureState?: DistanceMeasureState;

	/** Shared drag state for measurement points (works with both tools) */
	private draggingPointId: string | null = null;
	private draggingPointState: MeasurementState | DistanceMeasureState | null = null;
	private dragStartMouse: { x: number; y: number } | null = null;
	private dragStartPixel: google.maps.Point | null = null;
	private didDragPoint: boolean = false;
	private boundPointDragMove: (event: MouseEvent) => void;
	private boundPointDragEnd: (event: MouseEvent) => void;

	@observable public selectedVectorMoveDelta: Point = {
		x: 0,
		y: 0,
	};
	@observable public selectedVectorInitialMove: Point | null = null;

	@observable selectedCapture: CaptureFileJSONObject | null = null;

	name = 'TopologicalEditorMapOverlay';

	private boundVectorHandleMouseMove: (event: MouseEvent) => void;
	private boundVectorHandleMouseUp: (event: MouseEvent) => void;

	constructor(private options: ITopologicalEditorMapOverlayOptions) {
		super();

		makeObservable(this);

		const { collections, selectedCollectionRevision, measurementState, distanceMeasureState } = options;

		this.overlay = new google.maps.OverlayView();
		this.overlay.setMap(options.map);

		this.selectedCollectionRevision = selectedCollectionRevision || null;
		this.collections = collections || [];
		this.measurementState = measurementState;
		this.distanceMeasureState = distanceMeasureState;

		this.overlay.onAdd = () => {
			this.el = this.overlay.getPanes().overlayMouseTarget;
			Services.Render.mount(this.el, this, this.name);
		};

		this.overlay.onRemove = () => {
			if (this.el) {
				Services.Render.mount(this.el, null as any, this.name);
			}
		};

		this.overlay.draw = this.onDraw.bind(this);

		this.boundVectorHandleMouseMove = this.onVectorHandleDragMove.bind(this);
		this.boundVectorHandleMouseUp = this.onVectorHandleMouseUp.bind(this);

		this.boundPointDragMove = this.onPointDragMove.bind(this);
		this.boundPointDragEnd = this.onPointDragEnd.bind(this);
	}

	override onRemove() {
		if (this.el) {
			Services.Render.mount(this.el, null as any, this.name);
		}
		if (this.overlay) {
			this.overlay.setMap(null);
		}
	}

	@action.bound private updateState() {
		this.setZoom(this.options.map.getZoom());
		this.setBounds(this.options.map.getBounds());
	}

	@action setZoom(zoom: number) {
		this.zoom = zoom;
	}

	@action setBounds(bounds: google.maps.LatLngBounds) {
		const newBounds = bounds.toUrlValue();
		if (newBounds === this.boundString.get()) {
			return;
		}
		this.bounds = bounds;
		this.boundString.set(newBounds);
	}

	onDraw() {
		const reset = this.verifyProjection();

		this.updateState();

		if (reset) {
			Services.Render.render(this.el, this.view());
		}
	}

	@action public setCollections(newCollections: CollectionModel[]) {
		this.collections = newCollections.filter(Boolean);
	}

	@action public setSelectedCollectionRevision(revision: string | null) {
		this.selectedCollectionRevision = revision;
	}

	@action public setSelectedVectorId(id: string | null) {
		this.selectedVectorId = id;
	}

	@action public hide() {
		this.visible = false;
		this.overlay.setMap(null);
	}

	@action public show() {
		this.visible = true;
		this.overlay.setMap(this.options.map);
	}

	@action public setInitialMove(coords: Point) {
		this.selectedVectorInitialMove = coords;
	}

	@action public setInferredMoveDelta(coords: Point) {
		this.selectedVectorMoveDelta = {
			x: coords.x - this.selectedVectorInitialMove.x,
			y: coords.y - this.selectedVectorInitialMove.y,
		};
	}

	@action public setCapture(capture: CaptureFileJSONObject) {
		this.selectedCapture = capture;

		this.onDraw();
	}

	@action public resetMoveDelta() {
		this.selectedVectorMoveDelta = {
			x: 0,
			y: 0,
		};
	}

	@action private onVectorHandleMouseDown(event: MouseEvent) {
		if (this.measurementState && this.measurementState.isActive && !this.measurementState.isPaused) {
			return;
		}
		if (this.distanceMeasureState && this.distanceMeasureState.isActive) {
			return;
		}

		if (this.options.onVectorDragStart) {
			this.options.onVectorDragStart();
		}

		this.setInitialMove(pick(event, ['x', 'y']));

		window.addEventListener('mouseup', this.boundVectorHandleMouseUp);
		window.addEventListener('mousemove', this.boundVectorHandleMouseMove);
	}

	@action private onVectorHandleDragMove(event: MouseEvent) {
		this.setInferredMoveDelta(pick(event, ['x', 'y']));
	}

	@action private onVectorHandleMouseUp() {
		window.removeEventListener('mouseup', this.boundVectorHandleMouseUp);
		window.removeEventListener('mousemove', this.boundVectorHandleMouseMove);

		// See if any of the deltas have changed (ie not 0);
		if (Object.values(this.selectedVectorMoveDelta).some(Boolean)) {
			const vector = TopologicalPointCollectionCache.getCollectionVectors(this.selectedCollectionRevision).find(
				(k) => this.selectedCollectionRevision + k.field.id === this.selectedVectorId
			);

			const { x, y } = PointCache.getPoint(vector.target.latitude, vector.target.longitude);

			const terminal = new google.maps.Point(x + this.selectedVectorMoveDelta.x, y + this.selectedVectorMoveDelta.y);

			const { lat, lng } = this.overlay.getProjection().fromDivPixelToLatLng(terminal);

			const nextVector = IkeGeodesic.getVector(vector.field.collection.location, {
				latitude: lat(),
				longitude: lng(),
				altitude: 0,
			});

			// The cache stores anchor fields under the vector type — widen here so
			// the caller's type guard can narrow correctly.
			const field = vector?.field;
			if (field) {
				if (this.options.onVectorDragEnd) {
					this.options.onVectorDragEnd(field, nextVector);
				} else if (CollectionModelFieldAnchor.isAnchorField(field)) {
					// Defensive fallback (no callback wired) — treat the drag as a manual
					// bearing edit on the anchor: force 'Other', clear span links.
					field.Mutator.manual({
						...nextVector,
						collectionTool: 'VECTOR',
						placement: 'Other',
						relatedSpanIds: [],
					});
				} else {
					field.Mutator.manual(nextVector);
				}
			}

			TopologicalPointCollectionCache.dropCollectionVectors(this.selectedCollectionRevision);

			this.resetMoveDelta();
		}
	}

	@action public setCollectionHoveredRevision(revision: string | null) {
		this.collectionHoverRevision = revision;
	}

	@action public setVectorHoverId(id: string | null) {
		this.collectionAndVectorHoverId = id;
	}

	@action public setHideTitles(hide: boolean) {
		this.hideTitles = hide;
	}

	@action public setMeasurementState(state: MeasurementState) {
		this.measurementState = state;
	}

	private verifyProjection(): boolean {
		const projection = this.overlay.getProjection();
		if (projection == null) {
			PointCache.reset();
			return true;
		}

		PointCache.setProjection(projection);

		const point = projection.fromLatLngToDivPixel(this.origin);
		const oldPoint = PointCache.getPoint(this.origin.lat(), this.origin.lng());

		if (point.x !== oldPoint.x || point.y !== oldPoint.y) {
			PointCache.reset();

			return true;
		}

		return false;
	}

	canSeeFieldOfView(): boolean {
		return Services.Token.has(IkeToken.FEATURE_MAP_FIELD_OF_VIEW);
	}

	get nodes() {
		this.verifyProjection();

		const output: MithrilVNode[] = this.collections.reduce((acc, col) => {
			let location: LocationData;
			let collection: CollectionModel = col;
			if (AppStore.Collection.has(col.revision)) {
				collection = AppStore.Collection.get(col.revision);
			}

			location = collection.location;

			const point = PointCache.getGeoPoint({
				type: 'point',
				coordinates: [get(location, ['longitude'], 0), get(location, ['latitude'], 0)],
			});

			const vectors = TopologicalPointCollectionCache.getCollectionVectors(collection);

			const lines = vectors
				// Check to make sure that the vectors that are there actually exist before drawing them
				.filter((vector) => vector.field.value)
				.map(
					(vector) =>
						new MapLine({
							vector,
							initialPoint: point,
							isSelected:
								this.selectedVectorId === collection.revision + vector.field.id &&
								vector.field.collection.revision === this.selectedCollectionRevision,
							drawLabels: this.showVectorLabelsOnMap(),
							showVectorLabel: [this.collectionAndVectorHoverId, this.selectedVectorId].includes(
								collection.revision + vector.field.id
							),
							onClick: () => {
								this.options.onVectorClick(collection.revision, vector.field.id);

								this.setSelectedVectorId(collection.revision + vector.field.id);
							},
							onVectorMouseOver: () => {
								this.setVectorHoverId(collection.revision + vector.field.id);
							},
							onVectorMouseOut: () => {
								this.setVectorHoverId(null);
							},
							onVectorHandleMouseDown: vector.field.isCollectionLink()
								? noop
								: (event) => {
										event.preventDefault();
										event.stopPropagation();

										this.onVectorHandleMouseDown(event);
									},
							vectorHandleMoveDelta: this.selectedVectorMoveDelta,
						})
				);

			let poleAccuracy = TopologicalPointCollectionCache.getCollectionPoleAccuracy(collection);

			const pointView = MapPoint.view({
				collection,
				point,
				isCollectionSelected: collection.revision === this.selectedCollectionRevision,
				poleAccuracy,
				onClick: () => {
					if (CurrentState.CollectionLink.isLinking) {
						CurrentState.CollectionLink.completeLinking(collection);
						return;
					}
					this.options.onCollectionClick(collection.revision);

					this.setSelectedVectorId(null);
				},
				onCollectionMouseOver: () => {
					this.setCollectionHoveredRevision(collection.revision);
				},
				onCollectionMouseOut: () => {
					this.setCollectionHoveredRevision(null);
				},
				showCollectionTitle: this.collectionHoverRevision === collection.revision,
				showTitlesOnMap: this.showCollectionTitlesOnMap(),
			});

			const pointLayer = pointView
				? m(
						'div',
						{
							key: `layer-point-${collection.revision}`,
							className: classNames(CSS.point, {
								[CSS.hidden]: !this.visible,
							}),
						},
						pointView
					)
				: null;

			const lineLayer = lines.length
				? m(
						'div',
						{
							key: `layer-line-${collection.revision}`,
							className: classNames(CSS.line, {
								[CSS.hidden]: !this.visible,
							}),
						},
						lines.map((x) => x.nodes)
					)
				: null;

			return [lineLayer, ...acc, pointLayer];
		}, []);

		// Add measurement nodes
		output.push(...this.measurementNodes);

		// Add distance measure nodes
		output.push(...this.distanceMeasureNodes);

		const projection = this.overlay.getProjection();
		if (this.selectedCapture && this.selectedCapture.device && this.canSeeFieldOfView() && projection) {
			const { latitude, longitude } = this.selectedCapture.device;

			const point = projection.fromLatLngToDivPixel(
				new google.maps.LatLng({
					lat: latitude,
					lng: longitude,
				})
			);

			const photomeasureNodes = m(
				'div',
				{
					className: CSS.container,
					style: {
						left: `${point.x}px`,
						top: `${point.y}px`,
						zIndex: -1,
					},
				},
				[
					m(
						'div',
						{
							className: CSS.fov,
						},
						m('img', {
							src: assetPath('/images/fov-cone.svg'),
							className: CSS.fovSprite,
							style: {
								transform: `rotate(${this.selectedCapture.orientation.bearing}rad)`,
							},
						})
					),
					m(
						'div',
						{
							className: CSS.orca,
						},
						m('img', {
							src: assetPath('/images/ike-device.svg'),
							className: CSS.orcaIcon,
							style: {
								transform: `rotate(${this.selectedCapture.orientation.bearing}rad)`,
							},
						})
					),
					m(
						'div',
						{
							className: CSS.label,
						},
						IkeUnit.display(this.selectedCapture.distance, Services.UserSettings.units)
					),
				]
			);

			output.push(photomeasureNodes);
		}

		return output.filter(Boolean);
	}

	override view() {
		return m.fragment({}, this.nodes);
	}

	toggleViewTitles(): void {
		const next = !this.viewCollectionLabels;
		this.viewCollectionLabels = next;
		this.viewVectorLabels = next;
	}

	showCollectionTitlesOnMap(): boolean {
		return !this.hideTitles && this.viewCollectionLabels;
	}

	showVectorLabelsOnMap(): boolean {
		return !this.hideTitles && this.viewVectorLabels;
	}

	private worldPointToPixel(latLng: google.maps.LatLng): google.maps.Point | null {
		const projection = this.overlay.getProjection();
		if (!projection) {
			return null;
		}

		return projection.fromLatLngToDivPixel(latLng);
	}

	// ── Shared drag handlers for measurement points (both tools) ──

	private onPointMouseDown(state: MeasurementState | DistanceMeasureState, pointId: string, event: MouseEvent) {
		event.stopPropagation();
		event.preventDefault();

		const point = state.points.find((p) => p.id === pointId);
		if (!point) {
			return;
		}

		this.draggingPointId = pointId;
		this.draggingPointState = state;
		this.didDragPoint = false;
		this.dragStartMouse = { x: event.clientX, y: event.clientY };
		this.dragStartPixel = this.worldPointToPixel(point.location);

		window.addEventListener('mousemove', this.boundPointDragMove);
		window.addEventListener('mouseup', this.boundPointDragEnd);
	}

	private onPointDragMove(event: MouseEvent) {
		if (!this.draggingPointId || !this.draggingPointState || !this.dragStartMouse || !this.dragStartPixel) {
			return;
		}

		this.didDragPoint = true;

		const projection = this.overlay.getProjection();
		if (!projection) {
			return;
		}

		const dx = event.clientX - this.dragStartMouse.x;
		const dy = event.clientY - this.dragStartMouse.y;
		const newPixel = new google.maps.Point(this.dragStartPixel.x + dx, this.dragStartPixel.y + dy);

		const latLng = projection.fromDivPixelToLatLng(newPixel);
		if (latLng) {
			this.draggingPointState.updatePointLocation(this.draggingPointId, latLng);
		}
	}

	private onPointDragEnd() {
		this.draggingPointId = null;
		this.draggingPointState = null;
		this.dragStartMouse = null;
		this.dragStartPixel = null;
		window.removeEventListener('mousemove', this.boundPointDragMove);
		window.removeEventListener('mouseup', this.boundPointDragEnd);
	}

	// ── Shared rendering for measurement points and lines (both tools) ──

	private renderDraggablePoint(
		state: MeasurementState | DistanceMeasureState,
		point: MeasurementPoint,
		keyPrefix: string,
		onClick?: (e: Event) => void,
		title?: string
	): MithrilVNode | null {
		const pixel = this.worldPointToPixel(point.location);
		if (!pixel) {
			return null;
		}

		const pointNode = GenericMapPoint.view({
			key: `${keyPrefix}-inner-${point.id}`,
			point: new google.maps.Point(0, 0),
			backgroundColor: '#FFFFFF',
			additionalStyles: { cursor: 'grab', position: 'relative' },
			onClick: (e: Event) => {
				e.stopPropagation();
				// Only fire click if the point was not dragged
				if (!this.didDragPoint && onClick) {
					onClick(e);
				}
			},
		});

		return m(
			'div',
			{
				key: `${keyPrefix}-${point.id}`,
				style: { position: 'absolute', left: `${pixel.x}px`, top: `${pixel.y}px` },
				title: title || 'Drag to move',
				onmousedown: (e: MouseEvent) => this.onPointMouseDown(state, point.id, e),
			},
			pointNode
		);
	}

	private renderMeasurementLine(
		start: MeasurementPoint,
		end: MeasurementPoint,
		keyPrefix = 'measurement'
	): MithrilVNode[] | null {
		const startPixel = this.worldPointToPixel(start.location);
		const endPixel = this.worldPointToPixel(end.location);

		if (!startPixel || !endPixel) {
			return null;
		}

		const line = GenericMapLine.view({
			key: `${keyPrefix}-line-${start.id}-${end.id}`,
			start: startPixel,
			end: endPixel,
			color: '#FFFFFF',
			strokeWidth: '1px',
		});

		const startLoc = { latitude: start.location.lat(), longitude: start.location.lng(), altitude: 0 };
		const endLoc = { latitude: end.location.lat(), longitude: end.location.lng(), altitude: 0 };
		const vector = IkeGeodesic.getVector(startLoc, endLoc);
		const distance = IkeUnit.display(vector.distance, Services.UserSettings.units);

		const midX = (startPixel.x + endPixel.x) / 2;
		const midY = (startPixel.y + endPixel.y) / 2;

		const label = m(
			'div',
			{
				key: `${keyPrefix}-label-${start.id}-${end.id}`,
				style: {
					position: 'absolute',
					left: `${midX}px`,
					top: `${midY}px`,
					transform: 'translate(-50%, -50%)',
					backgroundColor: 'rgba(0, 0, 0, 0.7)',
					color: '#FFFFFF',
					padding: '2px 6px',
					borderRadius: '3px',
					fontSize: '11px',
					fontWeight: '500',
					whiteSpace: 'nowrap',
					pointerEvents: 'none',
					zIndex: 1,
				},
			},
			distance
		);

		return [line, label];
	}

	// ── Tool-specific node getters ──

	private get measurementNodes(): MithrilVNode[] {
		if (!this.measurementState || this.measurementState.points.length === 0) {
			return [];
		}

		const points = this.measurementState.points;
		const lineNodes = points.slice(0, -1).flatMap((point, index) => {
			const nextPoint = points[index + 1];
			if (!nextPoint) return [];
			return this.renderMeasurementLine(point, nextPoint) || [];
		});

		const pointNodes = points
			.map((point) =>
				this.renderDraggablePoint(
					this.measurementState!,
					point,
					'measurement',
					(e: Event) => {
						e.stopPropagation();
						this.measurementState!.removePoint(point.id);
					},
					Translate(T.MEASUREMENT_CLICK_TO_REMOVE)
				)
			)
			.filter(Boolean);

		return [...lineNodes, ...pointNodes];
	}

	private get distanceMeasureNodes(): MithrilVNode[] {
		if (!this.distanceMeasureState || this.distanceMeasureState.points.length === 0) {
			return [];
		}

		const points = this.distanceMeasureState.points;
		const pointNodes = points
			.map((point) => this.renderDraggablePoint(this.distanceMeasureState!, point, 'distance-measure'))
			.filter(Boolean);

		if (points.length >= 2) {
			const lineNodes = this.renderMeasurementLine(points[0]!, points[1]!, 'distance-measure') || [];
			return [...lineNodes, ...pointNodes];
		}

		return pointNodes;
	}
}
